import { useEffect, useState } from 'react';
import { PencilSimpleIcon, PlusIcon, TrashIcon } from '@phosphor-icons/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui-kit/button';
import { Input } from '@/components/ui-kit/input';
import { ConfirmationModal } from '@/components/core/confirmation-modal/confirmation-modal';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui-kit/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui-kit/dialog';
import {
  createApiKeyScopeRule,
  deleteApiKeyScopeRule,
  listApiKeyScopeRules,
  updateApiKeyScopeRule,
  type AccessReference,
  type ApiKeyScopeRule,
  type ApiKeyScopeRuleInput,
} from './api-key-scope-rules.service';
import { ApiKeyRoleCombobox } from './api-key-role-combobox';
import { getRoleRuleDefaults } from './api-key-scopes';

const emptyRule = (): ApiKeyScopeRuleInput => ({
  RoleSlug: '',
  GroupKey: '',
  GroupLabel: '',
  Label: '',
  IsGroupAll: false,
  SortOrder: 0,
  IsVisible: true,
  IsEnabled: true,
  Includes: [],
  Requires: [],
});

const RULES_PAGE_SIZE = 10;

function ReferenceEditor({
  label,
  organizationId,
  values,
  onChange,
}: {
  label: string;
  organizationId: string;
  values: AccessReference[];
  onChange: (values: AccessReference[]) => void;
}) {
  return (
    <fieldset className="space-y-2">
      <legend className="text-sm font-medium">{label}</legend>
      {values.map((reference, index) => (
        <div key={`${reference.Type}-${index}`} className="flex gap-2">
          <select
            aria-label={`${label} type ${index + 1}`}
            className="h-10 rounded-md border border-input bg-white px-2 text-sm"
            value={reference.Type}
            onChange={(event) => {
              const next = [...values];
              next[index] = {
                Type: event.target.value as AccessReference['Type'],
                Value: '',
              };
              onChange(next);
            }}
          >
            <option value="role">Role</option>
            <option value="permission">Permission</option>
          </select>
          <div className="min-w-0 flex-1">
            {reference.Type === 'role' ? (
              <ApiKeyRoleCombobox
                label={`${label} value ${index + 1}`}
                organizationId={organizationId}
                value={reference.Value}
                onChange={(Value) => {
                  const next = [...values];
                  next[index] = { ...reference, Value };
                  onChange(next);
                }}
              />
            ) : (
              <Input
                className="h-10"
                aria-label={`${label} value ${index + 1}`}
                placeholder="Exact permission resource"
                value={reference.Value}
                onChange={(event) => {
                  const next = [...values];
                  next[index] = { ...reference, Value: event.target.value };
                  onChange(next);
                }}
              />
            )}
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label={`Remove ${label} ${index + 1}`}
            onClick={() => onChange(values.filter((_, itemIndex) => itemIndex !== index))}
          >
            <TrashIcon size={16} />
          </Button>
        </div>
      ))}
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={() => onChange([...values, { Type: 'role', Value: '' }])}
      >
        <PlusIcon size={14} /> Add reference
      </Button>
    </fieldset>
  );
}

export function ApiKeyScopeRulesDialog({
  organizationId,
  onClose,
}: {
  organizationId: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const queryKey = ['api-key-scope-rules', organizationId];
  const {
    data = [],
    isPending,
    isError,
    refetch,
  } = useQuery({
    queryKey,
    queryFn: () => listApiKeyScopeRules(organizationId),
    retry: false,
  });
  const [editing, setEditing] = useState<ApiKeyScopeRule | 'new' | null>(null);
  const [form, setForm] = useState<ApiKeyScopeRuleInput>(emptyRule);
  const [error, setError] = useState('');
  const [deleting, setDeleting] = useState<ApiKeyScopeRule | null>(null);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const searchTerm = search.trim().toLowerCase();
  const filteredRules = data.filter((rule) =>
    [rule.RoleSlug, rule.GroupKey, rule.GroupLabel, rule.Label].some((value) =>
      value.toLowerCase().includes(searchTerm)
    )
  );
  const totalPages = Math.max(1, Math.ceil(filteredRules.length / RULES_PAGE_SIZE));
  const currentPage = Math.min(page, totalPages - 1);
  const pageRules = filteredRules.slice(
    currentPage * RULES_PAGE_SIZE,
    (currentPage + 1) * RULES_PAGE_SIZE
  );

  useEffect(() => {
    if (!editing) return;
    setError('');
    setForm(
      editing === 'new'
        ? emptyRule()
        : {
            RoleSlug: editing.RoleSlug,
            GroupKey: editing.GroupKey,
            GroupLabel: editing.GroupLabel,
            Label: editing.Label,
            IsGroupAll: editing.IsGroupAll,
            SortOrder: editing.SortOrder,
            IsVisible: editing.IsVisible,
            IsEnabled: editing.IsEnabled,
            Includes: editing.Includes ?? [],
            Requires: editing.Requires ?? [],
          }
    );
  }, [editing]);

  const invalidate = () => queryClient.invalidateQueries({ queryKey });
  const save = useMutation({
    mutationFn: async () => {
      const cleaned = {
        ...form,
        RoleSlug: form.RoleSlug.trim(),
        GroupKey: form.GroupKey.trim(),
        GroupLabel: form.GroupLabel.trim(),
        Label: form.Label.trim(),
        Includes: form.Includes.filter((item) => item.Value.trim()).map((item) => ({
          ...item,
          Value: item.Value.trim(),
        })),
        Requires: form.Requires.filter((item) => item.Value.trim()).map((item) => ({
          ...item,
          Value: item.Value.trim(),
        })),
      };
      if (!cleaned.RoleSlug || !cleaned.GroupKey || !cleaned.GroupLabel || !cleaned.Label) {
        throw new Error('Role slug, group key, group label and label are required.');
      }
      const references = [...cleaned.Includes, ...cleaned.Requires];
      if (references.some((item) => item.Type === 'role' && item.Value === cleaned.RoleSlug)) {
        throw new Error('A role cannot include or require itself.');
      }
      if (editing === 'new') await createApiKeyScopeRule(cleaned);
      else if (editing) await updateApiKeyScopeRule(editing.ItemId, cleaned);
    },
    onSuccess: async () => {
      await invalidate();
      setEditing(null);
    },
    onError: (value) => setError(value instanceof Error ? value.message : 'Could not save rule.'),
  });
  const remove = useMutation({
    mutationFn: deleteApiKeyScopeRule,
    onSuccess: invalidate,
  });

  return (
    <Dialog open onOpenChange={(open) => !open && !save.isPending && onClose()}>
      <DialogContent
        className="max-h-[90dvh] max-w-4xl gap-3 overflow-y-auto bg-white p-5"
        overlayClassName="bg-black/40"
      >
        <DialogHeader>
          <DialogTitle>Manage scope permissions</DialogTitle>
          <DialogDescription>
            Configure how IAM roles appear and which roles or permissions they include or require.
          </DialogDescription>
        </DialogHeader>
        <p className="rounded-md border border-primary/20 bg-primary/5 px-3 py-2 text-xs leading-relaxed text-muted-foreground">
          Role naming rule: create API-key scope roles using group_action, for example
          contacts_read or lists_write. Supported actions are all, read, write, and delete.
          Single-word roles such as admin, marketer, and owner are ignored.
        </p>
        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}
        {editing ? (
          <form
            className="space-y-5"
            onSubmit={(event) => {
              event.preventDefault();
              setError('');
              save.mutate();
            }}
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="space-y-2 text-sm font-medium">
                <span>Role slug</span>
                <ApiKeyRoleCombobox
                  label="Role slug"
                  organizationId={organizationId}
                  value={form.RoleSlug}
                  onChange={(RoleSlug, role) => {
                    const defaults = getRoleRuleDefaults(role);
                    setForm({ ...form, RoleSlug, ...(defaults ?? {}) });
                  }}
                />
              </label>
              {[
                ['Group key', 'GroupKey'],
                ['Group label', 'GroupLabel'],
                ['Label', 'Label'],
              ].map(([label, key]) => (
                <label key={key} className="space-y-2 text-sm font-medium">
                  <span>{label}</span>
                  <Input
                    value={String(form[key as keyof ApiKeyScopeRuleInput])}
                    onChange={(event) => setForm({ ...form, [key]: event.target.value })}
                  />
                </label>
              ))}
              <label className="space-y-2 text-sm font-medium">
                <span>Sort order</span>
                <Input
                  type="number"
                  value={form.SortOrder}
                  onChange={(event) => setForm({ ...form, SortOrder: Number(event.target.value) })}
                />
              </label>
              <div className="flex flex-wrap items-end gap-4 pb-3">
                {[
                  ['Group All', 'IsGroupAll'],
                  ['Visible', 'IsVisible'],
                  ['Enabled', 'IsEnabled'],
                ].map(([label, key]) => (
                  <label key={key} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={Boolean(form[key as keyof ApiKeyScopeRuleInput])}
                      onChange={(event) => setForm({ ...form, [key]: event.target.checked })}
                    />
                    {label}
                  </label>
                ))}
              </div>
            </div>
            <div className="grid gap-5 md:grid-cols-2">
              <ReferenceEditor
                label="Includes"
                organizationId={organizationId}
                values={form.Includes}
                onChange={(Includes) => setForm({ ...form, Includes })}
              />
              <ReferenceEditor
                label="Requires"
                organizationId={organizationId}
                values={form.Requires}
                onChange={(Requires) => setForm({ ...form, Requires })}
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                disabled={save.isPending}
                onClick={() => setEditing(null)}
              >
                Back
              </Button>
              <Button type="submit" loading={save.isPending}>
                Save rule
              </Button>
            </DialogFooter>
          </form>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <Input
                type="search"
                aria-label="Search scope rules"
                placeholder="Search by role, group or label"
                className="h-9 min-w-0 flex-1"
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setPage(0);
                }}
              />
              <Button className="shrink-0" onClick={() => setEditing('new')}>
                <PlusIcon size={16} /> Add rule
              </Button>
            </div>
            {isPending ? (
              <p role="status" className="py-10 text-center text-sm text-muted-foreground">
                Loading scope rules…
              </p>
            ) : isError ? (
              <div role="alert" className="py-10 text-center">
                <p>Couldn’t load scope rules.</p>
                <Button className="mt-3" variant="outline" onClick={() => void refetch()}>
                  Retry
                </Button>
              </div>
            ) : data.length === 0 ? (
              <p className="rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground">
                No scope rules configured yet.
              </p>
            ) : filteredRules.length === 0 ? (
              <p className="rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground">
                No scope rules match your search.
              </p>
            ) : (
              <div className="overflow-hidden rounded-md border">
                <Table aria-label="Scope permission rules" className="table-fixed">
                  <TableHeader>
                    <TableRow className="bg-neutral-50 hover:bg-neutral-50">
                      {['Role', 'Group', 'Label', 'Includes', 'Requires', 'Status'].map(
                        (heading) => (
                          <TableHead
                            key={heading}
                            className={
                              heading === 'Role'
                                ? 'h-10 w-[24%] px-2 text-xs font-semibold text-neutral-600'
                                : 'h-10 px-2 text-xs font-semibold text-neutral-600'
                            }
                          >
                            {heading}
                          </TableHead>
                        )
                      )}
                      <TableHead className="h-10 w-[72px] px-2 text-right text-xs font-semibold text-neutral-600">
                        Actions
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pageRules.map((rule) => (
                      <TableRow key={rule.ItemId}>
                        <TableCell className="px-2 py-1">
                          <code className="break-all text-xs">{rule.RoleSlug}</code>
                        </TableCell>
                        <TableCell className="break-words px-2 py-1 text-sm">
                          {rule.GroupLabel}
                        </TableCell>
                        <TableCell className="break-words px-2 py-1 text-sm">
                          {rule.Label}
                        </TableCell>
                        <TableCell className="px-2 py-1 text-center text-sm">
                          {rule.Includes?.length ?? 0}
                        </TableCell>
                        <TableCell className="px-2 py-1 text-center text-sm">
                          {rule.Requires?.length ?? 0}
                        </TableCell>
                        <TableCell className="break-words px-2 py-1 text-xs">
                          {rule.IsVisible && rule.IsEnabled
                            ? 'Visible · Enabled'
                            : `${rule.IsVisible ? 'Visible' : 'Hidden'} · ${rule.IsEnabled ? 'Enabled' : 'Disabled'}`}
                        </TableCell>
                        <TableCell className="px-2 py-1 text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon-sm"
                              aria-label={`Edit ${rule.RoleSlug}`}
                              onClick={() => setEditing(rule)}
                            >
                              <PencilSimpleIcon size={16} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon-sm"
                              className="text-destructive"
                              disabled={remove.isPending}
                              aria-label={`Delete ${rule.RoleSlug}`}
                              onClick={() => setDeleting(rule)}
                            >
                              <TrashIcon size={16} />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <nav
                  aria-label="Scope rules pagination"
                  className="flex items-center justify-between border-t bg-neutral-50 px-3 py-2"
                >
                  <span className="text-xs text-muted-foreground">
                    Page {currentPage + 1} of {totalPages}
                  </span>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      aria-label="Previous rules page"
                      disabled={currentPage === 0}
                      onClick={() => setPage((value) => Math.max(0, value - 1))}
                    >
                      Previous
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      aria-label="Next rules page"
                      disabled={currentPage + 1 >= totalPages}
                      onClick={() => setPage((value) => value + 1)}
                    >
                      Next
                    </Button>
                  </div>
                </nav>
              </div>
            )}
          </div>
        )}
        {deleting && (
          <ConfirmationModal
            open
            title={`Delete “${deleting.RoleSlug}”?`}
            description={
              <>
                <p>This soft-deletes the scope rule. API keys are not changed.</p>
                {remove.isError && (
                  <p role="alert" className="mt-3 text-destructive">
                    {remove.error instanceof Error ? remove.error.message : 'Could not delete rule.'}
                  </p>
                )}
              </>
            }
            confirmText={remove.isPending ? 'Deleting…' : 'Delete rule'}
            confirmClassName="bg-destructive"
            confirmDisabled={remove.isPending}
            preventAutoClose
            onOpenChange={(open) => {
              if (!open && !remove.isPending) {
                setDeleting(null);
                remove.reset();
              }
            }}
            onConfirm={() =>
              remove.mutate(deleting.ItemId, { onSuccess: () => setDeleting(null) })
            }
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
